import { ApiError } from "../api/api-error";
import type { TranslationKey } from "../i18n/translation";
import { describeServerMessage } from "./describe-server-message";
import { describeFieldErrors } from "./describe-field-errors";

/**
 * Maps a failed call to a translation key.
 *
 * Uses stable codes where available, then translates descriptions and field validation messages.
 * Unmapped responses use a localized status fallback.
 *
 * Sign-in failures are indistinguishable on the server on purpose. Do not add an entry that would
 * let a visitor tell "no such account" from "wrong password". <b>The three registration conflicts
 * below are the deliberate exception</b>, and the exception cannot be avoided: a registration that
 * silently succeeded on a taken number would either hand this person somebody else's account or
 * create a second one the sign-in query would then have to choose between. Each message says what
 * to do about it, because "try signing in instead" is the answer in every case.
 */

export function describeApiError(error: unknown): TranslationKey {
  if (!(error instanceof ApiError)) {
    return "error.generic";
  }

  if (error.isNetworkError) {
    return "error.network";
  }

  // Keyed on the status, not the description: 429 has exactly one meaning, and the rate
  // limiter's body is written by the framework rather than by a handler.
  if (error.status === 429) {
    return "error.too_many_requests";
  }

  const detail = error.problem?.detail;

  const code = error.problem?.code;
  if (code && Object.hasOwn(CODE_KEYS, code)) {
    return CODE_KEYS[code];
  }

  const described =
    detail === undefined ? undefined : describeServerMessage(detail);
  if (described) return described;

  const fields = Object.values(describeFieldErrors(error));
  if (fields.length > 0) return fields[0];

  if (error.status === 400) return "validation.invalid";
  if (error.status === 401) return "error.invalid_credentials";
  if (error.status === 403) return "error.forbidden";
  if (error.status === 404 || error.status === 410) return "error.not_found";
  if (error.status === 409) return "error.conflict";
  return "error.generic";
}

const CODE_KEYS: Record<string, TranslationKey> = {
  "Authentication.SessionNotFound": "error.session_ended",
  "Authentication.SessionExpired": "error.session_ended",
  "Authentication.NetworkChanged": "error.session_ended",
  "Authentication.LocationChanged": "error.session_ended",
  "Authentication.DeviceChanged": "error.session_ended",
  "Authentication.PositionJumped": "error.session_ended",
  "Authentication.SessionSuperseded": "error.session_superseded",
  "Authentication.CredentialsChanged": "error.session_ended",
  "Authentication.AccessWithdrawn": "error.portal_no_account",
  "Portal.NoAccount": "error.portal_no_account",
  "Portal.CaseNotFound": "error.case_not_found",
  "Portal.CustomerNotFound": "error.portal_no_account",
  "Portal.IinIsFixed": "error.portal_iin_fixed",
  "Portal.IinAlreadyInUse": "error.portal_iin_in_use",
  "Customer.InvalidInvitation": "error.invalid_invitation",
  "Customer.PhoneAlreadyInUse": "error.phone_already_in_use",
  "Customer.EmailAlreadyInUse": "error.email_already_in_use",
  "Customer.IinAlreadyInUse": "error.iin_already_in_use",
  "CaseDocument.NotFound": "error.document_not_found",
  "CaseDocument.VersionNotFound": "error.document_not_found",
  "CaseDocument.ContentMissing": "error.document_not_found",
};
